import { StatusId, OperationBC, CommandeRang, CommandeArticle, HistoriqueEvent } from '../types';

// Derivations PURES de l'onglet Offre acheteur-BC : articles formates, etat de
// presentation (bon de commande / refus), flags d'affichage du statut. Aucune
// dependance React. Gating par id de statut (les noms en DB peuvent changer).

const parseRang = (value: string | number): number => parseInt(String(value), 10);

const getCmd = (operation: OperationBC, rang: number): CommandeRang | undefined =>
  (operation.commandes || []).find((c) => parseRang(c.rang) === rang);

const toNumber = (value: number | string | undefined, fallback: number): number => {
  if (value === undefined || value === '') return fallback;
  const parsed = typeof value === 'number' ? value : parseFloat(value);
  return Number.isNaN(parsed) ? fallback : parsed;
};

// --- Articles de l'offre (tableau financier, lecture seule) -------------------
export interface OffreArticle {
  id: string | number;
  code: string;
  nom: string;
  description: string;
  unite: string;
  quantite: number;
  prixUnitaire: number;
  totalHT: number;
}

export const formatOffreArticles = (operation: OperationBC, rang: number): OffreArticle[] => {
  const cmd = getCmd(operation, rang);
  const articles: CommandeArticle[] = cmd?.articles || [];

  return articles.map((raw, index) => {
    const catalogue = raw.catalogue;
    const articleData = catalogue?.article;

    if (!catalogue || !articleData) {
      return {
        id: index,
        code: `Article ${index + 1}`,
        nom: 'Article non défini',
        description: '',
        unite: 'Unité',
        quantite: 1,
        prixUnitaire: 0,
        totalHT: 0,
      };
    }

    // L'info lot/departement peut vivre nichee (operation.operation.*) OU au top-level
    // (operation.* cote acheteur direct), exactement comme le sous-titre (OperationOffre.tsx:327-329).
    // Sans le fallback top-level, la reference tombait sur les defauts -> "00 000 X 1".
    const departementId =
      operation.operation?.lot_departement?.departement?.id ??
      operation.lot_departement?.departement?.id ??
      '00';
    const lotId =
      operation.operation?.lot_technique?.id ?? operation.lot_technique?.id ?? '000';
    const articleCode = catalogue.code ?? index;
    const reference = `${departementId.toString().padStart(2, '0')} ${lotId
      .toString()
      .padStart(3, '0')} ${articleCode} ${rang}`;

    return {
      id: articleData.id ?? catalogue.id ?? index,
      code: reference,
      nom: catalogue.nom || `Article ${index + 1}`,
      description: catalogue.description || '',
      unite: catalogue.unite || 'Unité',
      quantite: toNumber(articleData.quantity ?? articleData.quantite, 1),
      prixUnitaire: toNumber(articleData.prix_unitaire ?? articleData.prix, 0),
      totalHT: toNumber(articleData.prix_total ?? articleData.totalHT, 0),
    };
  });
};

export const totalOffreArticles = (articles: OffreArticle[]): number =>
  articles.reduce((sum, a) => sum + a.totalHT, 0);

// --- Etat de presentation de l'offre du rang ----------------------------------
export interface OffreRefusInfo {
  date: string;
  refusedBy: 'acheteur' | 'prestataire';
  acheteurNom: string;
  prestataireNom: string;
  rang: number;
  isExpired: boolean;
  isCancelled: boolean;
}

export interface OffreState {
  showBonCommande: boolean;
  showRefusMessage: boolean;
  refusInfo: OffreRefusInfo | null;
}

const refusedByFromHistorique = (cmd: CommandeRang): 'acheteur' | 'prestataire' => {
  const refusEvent: HistoriqueEvent | undefined = cmd.historique?.find(
    (h) =>
      h.prefix?.toLowerCase().includes('refus') || h.message?.toLowerCase().includes('refus'),
  );
  if (refusEvent) {
    if (
      refusEvent.message?.toLowerCase().includes('acheteur') ||
      refusEvent.prefix?.toLowerCase().includes('acheteur') ||
      refusEvent.type_compte === 'acheteur'
    ) {
      return 'acheteur';
    }
    return 'prestataire';
  }
  return cmd.type_compte === 'acheteur' ? 'acheteur' : 'prestataire';
};

export const computeOffreState = (operation: OperationBC, rang: number): OffreState => {
  const cmd = getCmd(operation, rang);
  if (!cmd) return { showBonCommande: false, showRefusMessage: false, refusInfo: null };

  const statusId = cmd.id_status;
  const isAccepted = statusId === StatusId.Valide;
  const isRefused = statusId === StatusId.Refuse;
  const isExpired = statusId === StatusId.Expire;
  const isCancelled = statusId === StatusId.Annule;
  const isRefusedOrExpiredOrCancelled = isRefused || isExpired || isCancelled;

  const acheteurNom = operation.organisme?.nom || operation.acheteur?.nom || 'Acheteur';
  const prestataireNom = cmd.contrat?.entite?.nom || 'Prestataire';

  return {
    showBonCommande: isAccepted,
    showRefusMessage: isRefusedOrExpiredOrCancelled,
    refusInfo: isRefusedOrExpiredOrCancelled
      ? {
          date: cmd.updated_at || '',
          refusedBy: isCancelled ? 'acheteur' : refusedByFromHistorique(cmd),
          acheteurNom,
          prestataireNom,
          rang,
          isExpired,
          isCancelled,
        }
      : null,
  };
};

// --- Flags d'affichage du statut ----------------------------------------------
export interface OffreStatusFlags {
  isAccepted: boolean;
  isRefused: boolean;
  isExpired: boolean;
  isPending: boolean;
}

// "En attente de l'acceptation du prestataire" : strictement par id, sur les
// statuts acheteur reels (AttenteAcceptationRang1/2/3 = 3/5/6). On NE matche aucun
// nom : ils peuvent etre d'anciens libelles, des statuts MS ou cote prestataire.
export const computeOffreStatusFlags = (
  operation: OperationBC,
  _rang: number,
): OffreStatusFlags => {
  const statusId = operation.status?.id;

  const isPending =
    statusId === StatusId.AttenteAcceptationRang1 ||
    statusId === StatusId.AttenteAcceptationRang2 ||
    statusId === StatusId.AttenteAcceptationRang3;

  return {
    isAccepted: statusId === StatusId.Valide,
    isRefused: statusId === StatusId.Refuse,
    isExpired: statusId === StatusId.Expire,
    isPending,
  };
};

// Horodatage francais complet : date ET heure (cf. pendant prestataire).
const horodatage = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.toLocaleDateString('fr-FR')} à ${d.toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
};

// Message de refus / expiration / annulation vu par l'ACHETEUR.
//
// Nomme TOUJOURS l'auteur et l'horodatage. Le cas "refus par le prestataire" se contentait de
// « Consultation refusée le ... » : l'acheteur, qui consulte plusieurs rangs, ne savait pas LEQUEL
// de ses titulaires avait refuse — l'information decide pourtant de la relance du rang suivant.
export const messageRefusAcheteur = (info: OffreRefusInfo): string => {
  const quand = horodatage(info.date);
  const suffixe = quand ? ` le ${quand}` : '';

  if (info.isCancelled) {
    return `Opération annulée par ${info.acheteurNom || "l'acheteur"}${suffixe}.`;
  }
  if (info.isExpired) {
    return `Offre expirée${suffixe} : le titulaire ${info.prestataireNom || 'de ce rang'} n'a pas répondu dans le délai imparti.`;
  }
  if (info.refusedBy === 'acheteur') {
    return `Vous avez refusé cette offre${suffixe}.`;
  }
  return `Consultation refusée par ${info.prestataireNom || 'le titulaire'} (rang ${info.rang})${suffixe}.`;
};

// --- Depot de l'offre par le prestataire --------------------------------------
// Statuts de la COMMANDE du rang ou le prestataire a effectivement repondu :
//   PropositionFaite(15) : il a chiffre et renvoye  ·  AttenteAnalyse(13) : offre deposee, analyse due
//   Valide(7) : offre acceptee                      ·  Refuse(8) : il a repondu NON, c'est une reponse
const STATUTS_OFFRE_DEPOSEE = [
  StatusId.PropositionFaite,
  StatusId.AttenteAnalyse,
  StatusId.Valide,
  StatusId.Refuse,
];

/**
 * Le prestataire de ce rang a-t-il repondu ?
 *
 * Tant que non, les sections « Offre technique » et « Offre financière » n'ont RIEN a montrer : elles
 * affichaient un tiret et un tableau vide, ce qui se lit comme une offre vide plutot que comme une
 * offre pas encore recue. On les verrouille a la place.
 *
 * Le STATUT de la commande fait foi, et lui seul. Aucun repli sur le contenu, pour deux raisons
 * verifiees plutot que supposees :
 *   - les ARTICLES ne prouvent rien : dans le panier classique c'est l'ACHETEUR qui les compose avant
 *     de lancer (4 commandes sur 4 au statut « A accepter » en portent deja) ;
 *   - l'OFFRE TECHNIQUE non plus : elle est enregistree au fil de la saisie (sauvegarde au blur), donc
 *     elle existe des le brouillon. S'y fier montrerait a l'acheteur un texte que le prestataire n'a
 *     pas encore soumis.
 * Le passage du statut est le seul evenement qui signe une reponse envoyee.
 */
export const offrePrestataireRecue = (operation: OperationBC, rang: number): boolean => {
  const cmd = (operation.commandes || []).find((c) => parseInt(String(c.rang), 10) === rang);
  if (!cmd) return false;
  return STATUTS_OFFRE_DEPOSEE.includes(cmd.id_status);
};
